import { SaleErrorCode } from '@/constants/saleConstraints';
import { formatCurrency } from '@utils/formatters';

/**
 * Canonical, user-facing copy for each sale error code (31-updated-sell-flow, web-client #205 / Task 2.4).
 *
 * Created here for client-side inline validation; dispatch 4 (#206) reuses this exact map to render
 * server-returned error codes — keep these strings stable so both paths show identical messages.
 */
export const SALE_ERROR_MESSAGES: Record<SaleErrorCode, string> = {
  BELOW_MIN_SALE: 'Sale amount must be at least £50,000',
  NOT_STEP_MULTIPLE: 'Sale amount must be in £10,000 increments',
  EXCEEDS_FACE_VALUE: 'Sale amount cannot exceed the face value',
  // #169 T8 — the remainder floor bites hardest between £50,000 and £100,000, where NO partial
  // sale is legal (e.g. £50,000 out of £90,000 leaves £40,000). The copy therefore points at the
  // remedy — sell the full holding — rather than only stating the rule.
  INVALID_REMAINDER:
    'Remaining holding must be £0 or at least £50,000. Try selling the full amount instead',
  INSUFFICIENT_HOLDING: 'Insufficient holding to process this sale',
  AMOUNT_NOT_POSITIVE: 'Enter a valid amount greater than zero',
  PURCHASE_NOT_SETTLED:
    "You'll be able to sell this bill from the business day after your purchase completes.",

  // Buy flow. Both directions share this copy: the user's remedy is to refresh and re-quote either
  // way, and the direction is an internal detail.
  PURCHASE_PRICE_BELOW_TOLERANCE: 'The price has moved. Refresh the price and try again',
  PURCHASE_PRICE_ABOVE_TOLERANCE: 'The price has moved. Refresh the price and try again',
  LISTING_UNAVAILABLE:
    'This bill is no longer available to buy. Choose another bill from the list.',

  // Net-of-fees funds guard (#161 / feature preservoir-ai#117).
  //
  // This entry is the NO-AMOUNT FALLBACK, not the primary copy. When the 402 carries a non-zero
  // `feeUnavailableAmount`, `mapSaleSubmitError` renders the amount-parameterised sentence built by
  // `formatInsufficientFundsMessage` instead — a static map entry cannot interpolate a figure,
  // which is why this code has a fourth registration point. This string is what the user sees when
  // the fee component is zero or absent (e.g. the legacy 2-arg server exception path, or a genuine
  // overspend with no accrued fees at all), where naming a "£0.00 unavailable" figure would be
  // worse than saying nothing.
  INSUFFICIENT_FUNDS: 'Insufficient funds to complete this request',
};

/**
 * The fee-gap explanation (#161 / feature preservoir-ai#117) — ONE definition, three renderers.
 *
 * Since #160b, every balance display shows GROSS (what the ledger holds) while every spend cap
 * stays on NET (what the client may actually spend). That is correct but reads as a bug: the user
 * sees £95,500.00 and is told £95,200 exceeds their balance. This sentence is the missing
 * explanation, and it must be identical wherever it appears, because a user can hit it from three
 * different directions for the same underlying reason:
 *
 *   • the withdrawal page's pre-submit cap        (client-side, primary)
 *   • the purchase page's pre-submit cap          (client-side, primary)
 *   • the server's 402 `feeUnavailableAmount`     (race/stale fallback — the accrued-fee figure
 *                                                  moved between the profile fetch and the submit)
 *
 * Amount always via the shared en-GB `formatCurrency` (2 dp) — never hand-rolled, so a pence-level
 * fee reads as "£1,234.50" rather than "£1234.5".
 *
 * @param feeUnavailableAmount the accrued-unpaid fee held back from the spendable balance
 */
export function formatInsufficientFundsMessage(feeUnavailableAmount: number): string {
  return `${formatCurrency(feeUnavailableAmount)} of your balance is unavailable due to outstanding fees`;
}

/**
 * Whether a fee figure is worth naming to the user.
 *
 * The wire always carries `feeUnavailableAmount` (the legacy 2-arg server exception ctor chains 0m
 * rather than omitting the key, so absent-vs-zero is never ambiguous), and the client-side caps
 * read `feesPayableBalance`, which is 0-defaulted by the mapper. Both channels therefore deliver a
 * legitimate zero for "no fees accrued" — which must fall back to generic copy, not render
 * "£0.00 of your balance is unavailable". A non-numeric value is wire corruption and is likewise
 * refused rather than formatted into "£NaN".
 */
export function hasFeeGap(
  feeUnavailableAmount: number | undefined,
): feeUnavailableAmount is number {
  return (
    typeof feeUnavailableAmount === 'number' &&
    Number.isFinite(feeUnavailableAmount) &&
    feeUnavailableAmount > 0
  );
}
